import { DEFAULT_RADII, type Theme } from '../tokens.js';

/**
 * Astral Crimson — the Crimson Celestial Atelier (default identity).
 *
 * An almost-black plum/wine canvas lit from within by burgundy → crimson →
 * dark-magenta fog, with pearl / pale-lavender / pink-white "moonlight"
 * highlights. The accent leans RED/WINE (not blue), and most surfaces stay very
 * dark so crimson reads like LIGHT and FOG rather than flat colour.
 */
export const astralCrimson: Theme = {
  id: 'astral-crimson',
  name: 'Astral Crimson',
  radii: DEFAULT_RADII,
  schemes: {
    dark: {
      background: '#070409',
      backgroundElevated: '#0E0710',
      // Celestial glass, matched to the Home cards so every surface in the app
      // shares one material: plum translucency the atmosphere shows through,
      // with a dusty-rose hairline rather than a grey border.
      surface: 'rgba(26, 12, 21, 0.56)',
      surfaceElevated: 'rgba(36, 17, 29, 0.72)',
      border: 'rgba(142, 74, 94, 0.22)',

      accent: '#A81E3C',
      accentHover: '#C42A4C',
      // Icon containers and soft tints across every screen read from this. It
      // was a crimson wash; it is now the same cool plum glass as Home's tiles,
      // which retires the last of the old red-tinted panels app-wide.
      accentSoft: 'rgba(40, 20, 56, 0.55)',
      accentContrast: '#FFF3F6',

      textPrimary: '#F5E8EE',
      textSecondary: '#C0A4B0',
      textMuted: '#847079',

      // "Positive/active" states use luminous rose, not generic green, so
      // Active badges / done markers / success toasts belong to the Astral
      // palette. `danger` and `warning` below deliberately stay distinct.
      success: '#E8709A',
      warning: '#E0A458',
      danger: '#E5484D',

      overlay: 'rgba(6, 3, 7, 0.66)',
      shadow: 'rgba(0, 0, 0, 0.6)',

      glow: '#D53A5C',
      accentGlow: 'rgba(213, 58, 92, 0.5)',
      metallic: 'rgba(255, 231, 240, 0.74)',
      textHighlight: '#FBE9F2',
      textShade: '#7C1730',
      borderGlow: 'rgba(196, 42, 76, 0.5)',
      surfaceHighlight: 'rgba(255, 236, 244, 0.06)',
      gradientStart: '#300B18',
      gradientMid: '#A81E3C',
      gradientEnd: '#E86E88',
    },
    light: {
      background: '#F8F1F4',
      backgroundElevated: '#FFFFFF',
      surface: 'rgba(255, 255, 255, 0.9)',
      surfaceElevated: '#FFFFFF',
      border: '#EBDCE1',

      accent: '#A81E3C',
      accentHover: '#8E1832',
      accentSoft: 'rgba(168, 30, 60, 0.1)',
      accentContrast: '#FFFFFF',

      textPrimary: '#1C1016',
      textSecondary: '#5E4C53',
      textMuted: '#8C7A81',

      success: '#C4526C',
      warning: '#C9873B',
      danger: '#D33A3F',

      overlay: 'rgba(28, 16, 22, 0.4)',
      shadow: 'rgba(28, 16, 22, 0.14)',

      glow: '#D53A5C',
      accentGlow: 'rgba(213, 58, 92, 0.3)',
      metallic: '#9B3350',
      textHighlight: '#C4526C',
      textShade: '#5E1226',
      borderGlow: 'rgba(168, 30, 60, 0.4)',
      surfaceHighlight: 'rgba(255, 255, 255, 0.8)',
      gradientStart: '#7A1730',
      gradientMid: '#A81E3C',
      gradientEnd: '#DA5570',
    },
  },
};
